import React, { useState, Fragment } from "react";
import {
  Box,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import CustomButton from "./CustomSubmitButton";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CustomPhoneNumberInput from "./CustomPhoneNumberInput";
import MultipleSelectChip from "./MultipleSelectChip";
import { StyledButton } from "@/components";

const errorStyle = {
  color: "#EF4444",
  fontSize: "0.875rem",
  marginTop: "5px",
};

const asteriskStyle = {
  color: "red",
  marginLeft: "4px",
};

const acceptedFileFormats = [
  "image/jpeg",
  "image/png",
  "application/pdf",
  "application/msword",
];

const CustomForm = ({ fields, onSubmit, buttons, component, children }) => {
  const theme = useTheme();
  const [fileName, setFileName] = useState("");
  const [fileError, setFileError] = useState("");

  const validationSchema = Yup.object(
    fields?.reduce((acc, field) => {
      if (field.required) {
        if (field.type === "email") {
          acc[field.name] = Yup.string()
            .email("Invalid email format")
            .required(field.validationMessage || "Required");
        } else if (field.type === "password") {
          acc[field.name] = Yup.string()
            .min(8, "Password must be at least 8 characters")
            .required(field.validationMessage || "Required");
        } else if (["text", "mobile", "textarea"].includes(field.type)) {
          acc[field.name] = Yup.string().required(
            field.validationMessage || "Required"
          );
        } else if (field.type === "date") {
          acc[field.name] = Yup.date().required(
            field.validationMessage || "Required"
          );
        } else if (field.type === "file") {
          acc[field.name] = Yup.mixed().required(
            field.validationMessage || "Required"
          );
        } else if (field.type === "select") {
          acc[field.name] = Yup.string().required(
            field.validationMessage || "Required"
          );
        } else if (field.type === "radio") {
          acc[field.name] = Yup.string().required(
            field.validationMessage || "Required"
          );
        } else if (field.type === "checkbox1") {
          acc[field.name] = Yup.string().required(
            field.validationMessage || "Required"
          );
        } else if (field.type === "time") {
          acc[field.name] = Yup.string().required(
            field.validationMessage || "Required"
          );
        } else if (field.type === "color") {
          acc[field.name] = Yup.string().required(
            field.validationMessage || "Required"
          );
        }
      }
      return acc;
    }, {})
  );

  return (
    <Fragment>
      <Formik
        initialValues={fields.reduce(
          (acc, field) => ({
            ...acc,
            [field.name]: field.type === "multipleSelect" ? [] : "",
          }),
          {}
        )}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => {
          const formData = new FormData();
          fields.forEach((field) => {
            if (field.type === "file") {
              formData.append(field.name, values[field.name]);
            } else {
              formData.append(field.name, values[field.name]);
            }
          });

          onSubmit(formData);
          resetForm();
          setFileName("");
          setFileError("");
        }}
      >
        {({
          values,
          setFieldValue,
          handleChange,
          handleBlur,
          handleSubmit,
          resetForm,
        }) => (
          <Box
            component={Form}
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              {fields.map((field) => {
                const fieldWidth =
                  component === "LeaveBalance"
                    ? field.fullWidth
                      ? "100%"
                      : "48%"
                    : field.fullWidth
                      ? "100%"
                      : "49%";

                // const defaultWidth =
                //   component === "LeaveBalance"
                //     ? field.fullWidth
                //       ? "100%"
                //       : "48%"
                //     : field.fullWidth
                //     ? "100%"
                //     : "49%";

                // const fieldWidth =
                //   field.type === "select" &&
                //   component === "CompOff" &&
                //   customWidths &&
                //   customWidths[field.name]
                //     ? customWidths[field.name]
                //     : defaultWidth;

                const renderLabel = (label) => (
                  <Typography
                    variant="subtitle2"
                    component="div"
                    sx={{
                      marginBottom: 1,
                      display: "flex",
                      alignItems: "center",
                      fontWeight: 700,
                      color: "#413D3D",
                      fontSize: "16px",
                    }}
                  >
                    {label}
                    {field.required && <span style={asteriskStyle}>*</span>}
                  </Typography>
                );

                switch (field.type) {
                  case "text":
                  case "email":
                  case "password":
                  case "textarea":
                    return (
                      <FormControl
                        key={field.name}
                        variant="outlined"
                        sx={{
                          marginBottom: 2,
                          width: fieldWidth,
                        }}
                      >
                        {renderLabel(field.label)}
                        <Field
                          as={TextField}
                          id={field.name}
                          name={field.name}
                          type={field.type}
                          variant="outlined"
                          multiline={field.type === "textarea"}
                          rows={field.type === "textarea" ? 2 : undefined}
                          placeholder={field.placeholder || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values[field.name]}
                          sx={{
                            "& .MuiInputBase-input": {
                              padding:
                                field.type === "textarea" ? "4px" : "10px",
                              height: "100%",
                              boxSizing: "border-box",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderRadius:
                                field.type === "textarea" ? "9.21px" : "4px",
                              borderWidth: "1px",
                            },
                          }}
                        />
                        <ErrorMessage
                          name={field.name}
                          component="div"
                          style={errorStyle}
                        />
                      </FormControl>
                    );

                  case "mobile":
                    return (
                      <FormControl
                        key={field.name}
                        variant="outlined"
                        sx={{
                          marginBottom: 2,
                          width: fieldWidth,
                          // background:"lightgrey",
                        }}
                      >
                        {renderLabel(field.label)}
                        <CustomPhoneNumberInput
                          onChange={(phone, country) => {
                            setFieldValue(field.name, phone);
                          }}
                          value={values[field.name]}
                          countryCode={field.countryCode}
                        />
                        <ErrorMessage
                          name={field.name}
                          component="div"
                          style={errorStyle}
                        />
                      </FormControl>
                    );

                  case "select":
                    return (
                      <FormControl
                        key={field.name}
                        variant="outlined"
                        sx={{
                          // marginBottom: 2,
                          width: fieldWidth,
                        }}
                      >
                        {renderLabel(field.label)}
                        <Field
                          as={Select}
                          labelId={`${field.name}-label`}
                          id={field.name}
                          name={field.name}
                          variant="outlined"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values[field.name]}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return <em>{field.placeholder}</em>;
                            }
                            return selected;
                          }}
                          sx={{
                            "& .MuiSelect-select": {
                              // height: '36px', // Adjust height of the select component
                              padding: "8px", // Adjust padding to fit within the reduced height
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderRadius: "4px",
                              borderWidth: "1px",
                            },
                            // Adjust menu height
                            "& .MuiMenuItem-root": {
                              fontSize: "14px", // Adjust font size if necessary
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            <em>{field.placeholder}</em>
                          </MenuItem>
                          {field.options.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Field>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            // marginTop: "5px",
                          }}
                        >
                          <ErrorMessage
                            name={field.name}
                            component="div"
                            style={errorStyle}
                          />
                          {field.balance && (
                            <Typography
                              variant="caption"
                              component="div"
                              sx={{
                                marginLeft: "auto",
                                fontFamily: "Lato",
                                fontSize: "16px",
                                fontWeight: 700,
                                lineHeight: "19.2px",
                                textAlign: "right",
                                marginTop: "5px",
                              }}
                            >
                              {field.balance.text}: {field.balance.count}
                            </Typography>
                          )}
                        </Box>
                      </FormControl>
                    );

                  case "radio":
                    return (
                      <FormControl
                        component="fieldset"
                        key={field.name}
                        sx={{
                          // marginBottom: 2,
                          width: fieldWidth,
                        }}
                      >
                        {renderLabel(field.label)}
                        <Field row as={RadioGroup} name={field.name}>
                          {field.options.map((option) => (
                            <FormControlLabel
                              key={option.value}
                              value={option.value}
                              control={<Radio />}
                              label={option.label}
                            />
                          ))}
                        </Field>
                        <ErrorMessage
                          name={field.name}
                          component="div"
                          style={errorStyle}
                        />
                      </FormControl>
                    );

                  case "checkbox":
                    return (
                      <FormControlLabel
                        key={field.name}
                        control={
                          <Field
                            as={Switch}
                            name={field.name}
                            checked={values[field.name]}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        }
                        label={renderLabel(field.label)}
                        sx={{
                          width: fieldWidth,
                        }}
                      />
                    );

                  case "date":
                    return (
                      <FormControl
                        key={field.name}
                        variant="outlined"
                        sx={{
                          // marginBottom: 2,
                          width: fieldWidth,
                        }}
                      >
                        {renderLabel(field.label)}
                        <Field
                          as={TextField}
                          id={field.name}
                          name={field.name}
                          type="date"
                          variant="outlined"
                          placeholder={field.placeholder || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values[field.name]}
                          sx={{
                            "& .MuiInputBase-input": {
                              padding: "7px",
                              height: "100%",
                              boxSizing: "border-box",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderRadius: "4px",
                              borderWidth: "1px",
                            },
                          }}
                        />
                        <ErrorMessage
                          name={field.name}
                          component="div"
                          style={errorStyle}
                        />
                      </FormControl>
                    );

                  case "multipleSelect":
                    return (
                      <FormControl
                        key={field.name}
                        variant="outlined"
                        sx={{
                          width: fieldWidth,
                        }}
                      >
                        {renderLabel(field.label)}
                        <MultipleSelectChip
                          field={field}
                          form={{ values, setFieldValue }}
                          options={field.options}
                          placeholder={field.placeholder}
                        />
                        <ErrorMessage
                          name={field.name}
                          component="div"
                          style={errorStyle}
                        />
                      </FormControl>
                    );

                  case "checkbox1":
                    return (
                      <FormControl
                        key={field.name}
                        component="fieldset"
                        sx={{
                          width: fieldWidth,
                          // marginBottom: 2,
                        }}
                      >
                        {renderLabel(field.label)}
                        <FormControlLabel
                          key={field.name}
                          control={
                            <Field
                              as={Checkbox}
                              name={field.name}
                              checked={values[field.name] || false}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          }
                          label={
                            <Typography variant="subtitle2">
                              {field.label}
                              {field.required && (
                                <span style={asteriskStyle}>*</span>
                              )}
                            </Typography>
                          }
                        />
                        <ErrorMessage
                          name={field.name}
                          component="div"
                          style={errorStyle}
                        />
                      </FormControl>
                    );

                  case "time":
                    return (
                      <FormControl
                        key={field.name}
                        variant="outlined"
                        sx={{
                          // marginBottom: 2,
                          width: fieldWidth,
                        }}
                      >
                        {renderLabel(field.label)}
                        <Field
                          as={TextField}
                          id={field.name}
                          name={field.name}
                          type="time"
                          variant="outlined"
                          placeholder={field.placeholder || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values[field.name]}
                        />
                        <ErrorMessage
                          name={field.name}
                          component="div"
                          style={errorStyle}
                        />
                      </FormControl>
                    );

                  case "color":
                    return (
                      <FormControl
                        key={field.name}
                        variant="outlined"
                        sx={{
                          // marginBottom: 2,
                          width: fieldWidth,
                        }}
                      >
                        {renderLabel(field.label)}
                        <Field
                          as={TextField}
                          id={field.name}
                          name={field.name}
                          type="color"
                          variant="outlined"
                          placeholder={field.placeholder || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values[field.name]}
                        />
                        <ErrorMessage
                          name={field.name}
                          component="div"
                          style={errorStyle}
                        />
                      </FormControl>
                    );

                  case "file":
                    return (
                      <FormControl
                        key={field.name}
                        variant="outlined"
                        sx={{
                          // marginBottom: 2,
                          width: fieldWidth,
                        }}
                      >
                        {renderLabel(field.label)}
                        <input
                          accept=".jpg,.jpeg,.png,.pdf,.doc"
                          id={field.name}
                          type="file"
                          name={field.name}
                          style={{ display: "none" }}
                          onChange={(event) => {
                            const file = event.currentTarget.files[0];
                            if (
                              file &&
                              acceptedFileFormats.includes(file.type)
                            ) {
                              setFieldValue(field.name, file);
                              setFileName(file.name);
                              setFileError("");
                            } else {
                              setFieldValue(field.name, null); // Clear the file field
                              setFileName("");
                              setFileError(
                                "Invalid file type. Please select a JPG, JPEG, PNG, PDF, or DOC file."
                              );
                            }
                          }}
                        />
                        <label htmlFor={field.name}>
                          <Button
                            component="span"
                            startIcon={<AttachFileIcon />}
                            fullWidth
                            sx={{
                              justifyContent: "flex-start",
                              border: "1px solid #D8D8D8",
                              color: "#000000",
                            }}
                          >
                            {fileName || field.placeholder}
                          </Button>
                        </label>
                        {fileError && (
                          <Typography
                            variant="body2"
                            color="error"
                            sx={{ marginTop: 1 }}
                          >
                            {fileError}
                          </Typography>
                        )}
                        {field.message && (
                          <Typography
                            variant="body2"
                            // color="error"
                            sx={{ marginTop: 1 }}
                          >
                            {field.message}
                          </Typography>
                        )}
                        <ErrorMessage
                          name={field.name}
                          component="div"
                          style={errorStyle}
                        />
                      </FormControl>
                    );



                  default:
                    return null;
                }
              })}
            </Box>

            {children}
            <StyledButton label={"Submit"} onClick={handleSubmit} size="large" type="submit" variant="contained" />
            {/* <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              {buttons?.map((button, index) => (
                <CustomButton
                  size={button.size}
                  key={index}
                  onClick={
                    button.type === "clear" ||
                    button.type === "reset" ||
                    button.type === "cancel"
                      ? () => resetForm()
                      : handleSubmit
                  }
                  title={button.label}
                  variant={button.variant}
                />
              ))}
            </Box> */}
          </Box>
        )}
      </Formik>
    </Fragment>
  );
};

export default CustomForm;
